import { PropertyHelper } from '@/helpers/property.helper';
import { AppConfigService } from '@/services/app-config.service';
import { BridgeInputService } from '@/services/bridge/bridge-input.service';
import { BridgeService } from '@/services/bridge/bridge.service';
import { FileToUpload, FileUploadService, FileUploadType } from '@/services/file-upload.service';
import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import {
  DocumentCategory,
  DocumentType,
  PhotoCategory,
  PropertiesService,
  PropertyCategory,
  RealProperty,
  TrackingService
} from '@eemo/api-client';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { FileSizePipe } from '../../extensions/fileSizePipe';
import { MatIconModule } from '@angular/material/icon';

import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { DragDropDirective } from '../../extensions/dragDropDirective';
import { NgTemplateOutlet, NgIf, NgFor, AsyncPipe } from '@angular/common';
import { ModalBaseComponent } from '../base/modal-base/modal-base.component';
import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export interface FileUpdloadDialogData {
  uploadType: FileUploadType;
  props: RealProperty[];
  defaultCategory?: PropertyCategory;
}

export interface LinkedObject {
  uid: string;
  name: string;
}

interface FileUploadControl {
  file: File;
  fileNameControl: FormControl<string>;
  formControlName: string;
}

@Component({
    selector: 'app-file-upload-dialog',
    templateUrl: './file-upload-dialog.component.html',
    styleUrls: ['./file-upload-dialog.component.scss'],
    standalone: true,
    imports: [ModalBaseComponent, NgTemplateOutlet, NgIf, MatDialogModule, DragDropDirective, TooltipDirective, MatButtonModule, MatFormFieldModule, MatSelectModule, NgFor, MatOptionModule, MatProgressBarModule, MatInputModule, FormsModule, ReactiveFormsModule, MatIconModule, AsyncPipe, FileSizePipe, MatProgressSpinnerModule],
    providers: [FileUploadService]
})
export class FileUploadDialogComponent implements OnInit, OnDestroy {
  @ViewChild('file') file: ElementRef;

  public files: Set<FileUploadControl> = new Set();

  progress: { [filename: string]: { progress: Observable<number> } };
  uploadErrors: any;
  uploadCompleted: any;
  canBeClosed: boolean;
  dialogTitle: string;
  uploadButtonText: string;
  uploadDescText: string;
  submitText: string;
  showCancelButton: boolean;
  uploading: boolean;
  uploadSuccessful: boolean;
  prepareFilesForUpload: boolean;
  uploadError: boolean;
  uploadEnded: boolean;
  acceptFileFormats: string[] = [];
  acceptFileFormatsMessage: string;
  errorMessage: string;

  uploadDoc = false;
  uploadForm: UntypedFormGroup;

  availableDocTypes: string[];
  availableDocTypeNames: { [key: string]: string } = {};
  availableDocCategories: DocumentCategory[];
  availableDocCategoryNames: { [key: string]: string } = {};
  availablePhotoCategories: string[];
  availablePhotoCategoryNames: { [key: string]: string } = {};

  docType: DocumentType;
  docCategory: DocumentCategory;
  photoCategory: PhotoCategory;

  showLinkedObject = false;
  linkObjectId: string;
  linkedObjects: LinkedObject[];

  FileUploadType = FileUploadType;
  newScanDocSub = new Subscription();

  progressSubs: Subscription[] = [];
  allProgressesSub = new Subscription();

  form: FormGroup;

  constructor(
    private helper: PropertyHelper,
    private dialogRef: MatDialogRef<FileUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FileUpdloadDialogData,
    private uploadService: FileUploadService,
    private propService: PropertiesService,
    private trackingService: TrackingService,
    private ref: ChangeDetectorRef,
    private bridgeInputService: BridgeInputService,
    private formBuilder: FormBuilder,
    private appConfigService: AppConfigService
  ) {
    this.form = this.formBuilder.group({});
    this.availableDocTypes = helper.getDocumentsTypes();
    this.availableDocTypes.forEach(
      (t) => (this.availableDocTypeNames[t] = helper.getDocumentTypeName(t as DocumentType))
    );
    this.availableDocCategories = helper.getDocumentCategories();
    this.availableDocCategories.forEach(
      (c) =>
        (this.availableDocCategoryNames[c] = helper.getDocumentCategoryName(
          c,
          data.props.length > 1 ? null : data.props[0].type
        ))
    );
    this.availablePhotoCategories = helper.getPhotoCategories();
    this.availablePhotoCategories.forEach(
      (p) => (this.availablePhotoCategoryNames[p] = helper.getPhotoCategoryName(p as PhotoCategory))
    );
  }

  ngOnInit() {
    this.initSubs();
    this.uploadDescText = this.appConfigService.isDesktop ? 'ou glissez-les ici' : null;
    if (this.data.uploadType === FileUploadType.DOC) {
      this.dialogTitle = 'Ajouter des documents';
      this.uploadButtonText = 'Choisir des fichiers';
      this.uploadDoc = true;
      this.acceptFileFormatsMessage = '';
    } else if (this.data.uploadType === FileUploadType.SCAN_DOC) {
      this.dialogTitle = 'Scanner des documents';
      this.uploadButtonText = 'Scanner';
      this.uploadDoc = true;
      this.acceptFileFormatsMessage = '';
      this.startScan();
    } else {
      this.dialogTitle = 'Ajouter des photos';
      this.uploadButtonText = 'Choisir des photos';
      this.uploadDoc = false;
      this.acceptFileFormats.push('image/png');
      this.acceptFileFormats.push('image/jpeg');
      this.acceptFileFormatsMessage = 'Seules les images JPEG et PNG sont autorisées';
    }

    this.docCategory = this.data.defaultCategory ? this.data.defaultCategory : PropertyCategory.BUILDING;
    this.docType = DocumentType.MAP;
    this.photoCategory = PhotoCategory.OUTSIDE;

    this.initState();
  }

  ngOnDestroy(): void {
    this.newScanDocSub.unsubscribe();
    this.progressSubs.forEach((s) => s.unsubscribe());
    this.allProgressesSub.unsubscribe();
  }

  initSubs() {
    this.newScanDocSub = this.bridgeInputService.newScanDoc$.subscribe(async (encodedFile64: string) => {
      const base64Response = await fetch(encodedFile64);
      const blob = await base64Response.blob();
      const newFile: File = new File([blob], 'Document ' + this.files.size + 1);
      this.addFileToUpload(newFile, true);
      this.ref.detectChanges();
    });
  }

  isPhoto() {
    return this.data.uploadType === FileUploadType.PHOTO;
  }
  isDoc() {
    return this.data.uploadType === FileUploadType.DOC;
  }

  initState() {
    this.uploadErrors = {};
    this.uploadCompleted = {};
    this.canBeClosed = true;
    this.submitText = 'START';
    this.showCancelButton = true;
    this.uploading = false;
    this.prepareFilesForUpload = false;
    this.uploadSuccessful = false;
    this.uploadError = false;
    this.uploadEnded = false;
    this.progress = undefined;
    this.onCategoryChange();
  }

  addFiles(event: FileList) {
    // if uploading, don't accept new file
    if (this.uploading) {
      return;
    }

    if (this.uploadEnded) {
      // an upload have aleady be done : reinit for a new upload
      this.initState();
      this.files = new Set();
    }
    this.errorMessage = '';
    for (let index = 0; index < event.length; index++) {
      const file = event.item(index);
      if (this.acceptFileFormats.length === 0 || this.acceptFileFormats.includes(file.type)) {
        this.addFileToUpload(file, false);
      } else {
        this.errorMessage = this.acceptFileFormatsMessage;
      }
    }
  }

  async addFileToUpload(file: File, fromScan: boolean) {
    // clone the file for android os
    // because when a file is selected from google drive the upload failed with the error net::ERR_UPLOAD_FILE_CHANGED
    // which seems to mean that the file changed datetime has been updated between the selection and the upload start
    if (!fromScan && this.appConfigService.isOSAndroid) {
      this.prepareFilesForUpload = true;
      const buffer = await file.arrayBuffer();
      file = new File([buffer], file.name, { type: file.type });
      this.prepareFilesForUpload = false;
    }

    const fileNameControl = new FormControl();
    fileNameControl.addValidators(Validators.required);
    fileNameControl.setValue(file.name);
    const formControlName = file.name + (this.files.size + 1);
    this.files.add({ file, fileNameControl, formControlName });
    this.form.addControl(formControlName, fileNameControl);
  }

  deleteFile(file: FileUploadControl) {
    this.files.delete(file);
    this.form.removeControl(file.formControlName);
  }

  startUploadOrTerminate() {
    // if everything was uploaded already, just close the dialog
    if (this.uploadEnded) {
      return this.dialogRef.close(this.uploadSuccessful);
    }

    if (!this.form.valid) {
      return;
    }

    // set the component state to "uploading"
    this.uploading = true;

    // build the file to upload
    const filesToUpload: Set<FileToUpload> = new Set();
    this.files.forEach((f) => {
      f.fileNameControl.disable();
      filesToUpload.add({ file: f.file, name: f.fileNameControl.value });
    });
    // start the upload and save the progress map
    const options =
      this.data.uploadType === FileUploadType.DOC || this.data.uploadType === FileUploadType.SCAN_DOC
        ? { category: this.docCategory, type: this.docType, linkObjectId: this.linkObjectId }
        : { category: this.photoCategory };
    this.progress = this.uploadService.upload(this.data.uploadType, this.data.props, options, filesToUpload);

    // convert the progress map into an array
    const allProgressObservables: Observable<number>[] = [];
    Object.keys(this.progress).forEach((key) => {
      allProgressObservables.push(this.progress[key].progress);
      this.progressSubs.push(
        this.progress[key].progress.subscribe(
          (data) => {},
          (err) => {
            this.uploadErrors[key] = err;
          },
          () => {
            this.uploadCompleted[key] = true;
          }
        )
      );
    });

    // Adjust the state variables

    // The OK-button should have the text "Finish" now
    this.submitText = 'TERMINATE';

    // The dialog should not be closed while uploading
    this.canBeClosed = false;
    this.dialogRef.disableClose = true;

    // Hide the cancel-button
    this.showCancelButton = false;

    // When all progress-observables are completed...
    this.allProgressesSub = forkJoin(allProgressObservables).subscribe({
      next: (next) => {
        this.canBeClosed = true;
        this.dialogRef.disableClose = false;
        this.uploadSuccessful = true;
        this.uploading = false;
        this.uploadEnded = true;
      },
      error: (err) => {
        this.canBeClosed = true;
        this.dialogRef.disableClose = false;
        this.uploadSuccessful = false;
        this.uploading = false;
        this.uploadEnded = true;
      }
    });
  }

  onCategoryChange() {
    this.linkedObjects = [];
    this.showLinkedObject = false;
    if (this.data.props.length === 1) {
      if (this.docCategory === DocumentCategory.WORK) {
        this.propService.getRealPropertyWorkList(this.data.props[0].uid).subscribe((data) => {
          this.showLinkedObject = data.length > 0;
          data.forEach((i) => this.linkedObjects.push({ uid: i.uid, name: i.summary }));
        });
      }
      if (this.docCategory === DocumentCategory.MAINTENANCE) {
        this.trackingService.getRealPropertyMaintenanceList(this.data.props[0].uid).subscribe((data) => {
          this.showLinkedObject = data.length > 0;
          data.forEach((i) => this.linkedObjects.push({ uid: i.uid, name: i.summary }));
        });
      }
    }
  }

  startScan() {
    BridgeService.launchScan();
  }
}
